import { csvDocument } from '../../lib/csv';
import type { CustomerAllocationRow } from './operational-summaries';

export interface CsvColumn {
  key: string;
  label: string;
  /** A column shown with a placeholder ("Coming soon") has no data to export. */
  placeholder?: string;
}

export interface AllocationCsvOptions {
  /** YYYY-MM the camera view's monthly columns cover; only for that view. */
  month?: string | undefined;
  /** The last day with heartbeats in that month. */
  throughDay?: string | undefined;
}

type Level = 'Customer' | 'Country' | 'Region' | 'Site' | 'Total';

/**
 * The customer allocation table as CSV (tidaltech/koi#8469): one row per
 * customer, country, region and site, in the order the table nests them,
 * then a total. Columns without data (a placeholder) are left out.
 */
export function allocationCsv(
  rows: readonly CustomerAllocationRow<Readonly<Record<string, number>>>[],
  columns: readonly CsvColumn[],
  options: AllocationCsvOptions = {},
): string {
  const exported = columns.filter((column) => !column.placeholder);
  const monthly = options.month !== undefined;
  const header = [
    'Level',
    'Customer',
    'Country',
    'Region',
    'Site',
    ...(monthly ? ['Month', 'Data through'] : []),
    ...exported.map((column) => column.label),
  ];
  const lines: (string | number)[][] = [header];
  const add = (
    level: Level,
    names: [string, string, string, string],
    counts: Readonly<Record<string, number>>,
  ) => {
    lines.push([
      level,
      ...names,
      ...(monthly ? [options.month ?? '', options.throughDay ?? ''] : []),
      ...exported.map((column) => counts[column.key] ?? 0),
    ]);
  };
  const totals: Record<string, number> = {};
  for (const customer of rows) {
    add('Customer', [customer.customerName, '', '', ''], customer.counts);
    for (const [key, count] of Object.entries(customer.counts)) {
      totals[key] = (totals[key] ?? 0) + count;
    }
    for (const country of customer.countries) {
      add('Country', [customer.customerName, country.label, '', ''], country.counts);
      for (const region of country.regions) {
        add('Region', [customer.customerName, country.label, region.label, ''], region.counts);
        for (const site of region.sites) {
          add(
            'Site',
            [customer.customerName, country.label, region.label, site.label],
            site.counts,
          );
        }
      }
    }
  }
  add('Total', ['', '', '', ''], totals);
  return csvDocument(lines);
}

/** e.g. camera-allocation-2026-10-through-2026-10-26.csv */
export function allocationCsvFilename(
  view: 'devices' | 'cameras',
  options: AllocationCsvOptions,
  today: Date = new Date(),
): string {
  if (view === 'cameras' && options.month) {
    const through = options.throughDay ? `-through-${options.throughDay}` : '';
    return `camera-allocation-${options.month}${through}.csv`;
  }
  const prefix = view === 'cameras' ? 'camera' : 'device';
  return `${prefix}-allocation-${today.toISOString().slice(0, 10)}.csv`;
}
